import { useNavigate, Navigate } from "react-router-dom";
import {
  useConversations,
  useCreateConversation,
} from "@agentic_conversation/api/conversation";
import { useAuth } from "@auth/frontend";
import { ImpersonationBadge } from "@agentic_system/components/impersonation-badge";
import {
  Button,
  EmptyState,
  ItemTable,
  ItemTableBody,
  ItemTableRow,
  ItemTableCell,
  ItemTableTitle,
  ItemTableSubtitle,
  ItemTableAvatar,
  ItemTableLoading,
  Spinner,
} from "@ui";
import { MessageCircle, Plus } from "lucide-react";

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function Conversations() {
  const navigate = useNavigate();
  const { user, admin, isLoading: authLoading } = useAuth();
  const { data: conversations, isLoading } = useConversations();
  const createConversation = useCreateConversation();

  // Auth loading
  if (authLoading) {
    return (
      <div
        data-testid="conversations"
        className="flex flex-col h-full bg-slate-50 min-h-0 relative"
      >
        <div className="flex-1 flex items-center justify-center">
          <Spinner size="lg" />
        </div>
      </div>
    );
  }

  // No user - redirect to impersonate
  if (!user) {
    return <Navigate to="/system/agentic" replace />;
  }

  const handleNewConversation = async () => {
    const conv = await createConversation.mutateAsync("New Chat");
    navigate(`/system/agentic/conversation/${conv.id}`);
  };

  const handleSelectConversation = (id: number) => {
    navigate(`/system/agentic/conversation/${id}`);
  };

  const hasConversations = (conversations?.length ?? 0) > 0;

  return (
    <div data-testid="conversations" className="h-full min-h-0 flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">
              Historique des conversations
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {admin && <ImpersonationBadge />}
            <Button
              icon={Plus}
              onClick={handleNewConversation}
              loading={createConversation.isPending}
            >
              Nouvelle conversation
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto px-6 py-6">
          {isLoading ? (
            <ItemTable>
              <ItemTableLoading rows={5} />
            </ItemTable>
          ) : hasConversations ? (
            <ItemTable>
              <ItemTableBody>
                {conversations?.map((conv) => (
                  <ItemTableRow
                    key={conv.id}
                    onClick={() => handleSelectConversation(conv.id)}
                    className="grid-cols-[1fr_auto]"
                  >
                    <ItemTableCell>
                      <ItemTableAvatar icon={MessageCircle} />
                      <div className="min-w-0">
                        <ItemTableTitle>
                          {conv.title || conv.label || "Conversation sans titre"}
                        </ItemTableTitle>
                        <ItemTableSubtitle>
                          {conv.messages_count ?? 0} message
                          {(conv.messages_count ?? 0) !== 1 ? "s" : ""}
                        </ItemTableSubtitle>
                      </div>
                    </ItemTableCell>
                    <ItemTableCell className="text-sm text-slate-500">
                      {formatDate(conv.updated_at)}
                    </ItemTableCell>
                  </ItemTableRow>
                ))}
              </ItemTableBody>
            </ItemTable>
          ) : (
            <EmptyState
              icon={MessageCircle}
              title="Aucune conversation"
              description="Démarrez une nouvelle conversation avec l'assistant IA"
              action={
                <Button
                  icon={Plus}
                  onClick={handleNewConversation}
                  loading={createConversation.isPending}
                >
                  Nouvelle conversation
                </Button>
              }
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default Conversations;
